import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Button } from "@/components/ui/button";

let cache = null;
const getStripe = (pk) => {
  if (!cache || cache._pk !== pk) { cache = loadStripe(pk); cache._pk = pk; }
  return cache;
};

function PayForm({ pending, client, onPaid, onCancel, t }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    timer.current = setInterval(async () => {
      try {
        const { data } = await client.get(`/public/recharge/status/${pending.reference}`);
        if (data.status === "paid") { clearInterval(timer.current); onPaid(data); }
        else if (data.status === "error") { clearInterval(timer.current); setWaiting(false); setError(data.error || "Payment could not be completed. Please visit Registration."); }
        else if (data.status === "cancelled") { clearInterval(timer.current); }
      } catch (e) { /* keep polling */ }
    }, 2500);
    return () => clearInterval(timer.current);
  }, [pending.reference, client, onPaid]);

  const pay = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true); setError(null);
    const { error: err } = await stripe.confirmPayment({ elements, confirmParams: { return_url: window.location.href }, redirect: "if_required" });
    if (err) setError(err.message || "Payment failed. You were not charged.");
    else setWaiting(true);
    setBusy(false);
  };

  return (
    <form onSubmit={pay} className="space-y-4" data-testid="recharge-pay-form">
      <div className="rounded-xl bg-white p-4"><PaymentElement options={{ layout: "tabs" }} /></div>
      {error && <div className="text-sm text-rose-300" data-testid="recharge-pay-error">{t(error)}</div>}
      {waiting && <div className="text-sm text-amber-200 flex items-center gap-2" data-testid="recharge-pay-waiting"><Loader2 className="animate-spin" size={14} /> {t("Confirming your payment with Stripe…")}</div>}
      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={busy || waiting} className="flex-1 h-12 bg-slate-800 border-slate-700 text-slate-200" data-testid="recharge-cancel-btn">{t("Cancel")}</Button>
        <Button type="submit" disabled={!stripe || busy || waiting} className="flex-1 h-12 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold" data-testid="recharge-pay-btn">
          {busy ? <Loader2 className="animate-spin" size={16} /> : `${t("PAY")} $${pending.total_amount.toFixed(2)}`}
        </Button>
      </div>
      <p className="text-[11px] text-slate-500">{t("Secure payment by Stripe. Your drinks are added after Stripe confirms the payment.")}</p>
    </form>
  );
}

export function RechargePay(props) {
  const { pending, lang } = props;
  return (
    <Elements key={lang} stripe={getStripe(pending.publishable_key)} options={{ clientSecret: pending.client_secret, appearance: { theme: "stripe" }, locale: lang === "es" ? "es" : "en" }}>
      <PayForm {...props} />
    </Elements>
  );
}
